import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Global keyboard shortcuts hook
 * Usage: useKeyboardShortcuts() in your App or layout component
 *
 * Shortcuts (all use Ctrl/Cmd + key):
 *   Ctrl+K  → Focus search bar
 *   Ctrl+/  → Show shortcuts help
 *   G then H → Go to Home/Dashboard
 *   G then O → Go to Orders
 *   G then C → Go to Catalog
 *   G then R → Go to Reports
 *   G then S → Go to Settings
 *   G then T → Go to Templates
 *   Escape  → Close modals/dialogs
 */

const SHORTCUTS = [
  { keys: 'Ctrl+K', description: 'Focus search bar' },
  { keys: 'Ctrl+/', description: 'Show keyboard shortcuts' },
  { keys: 'G → H', description: 'Go to Dashboard' },
  { keys: 'G → O', description: 'Go to Orders' },
  { keys: 'G → C', description: 'Go to Catalog' },
  { keys: 'G → R', description: 'Go to Reports' },
  { keys: 'G → S', description: 'Go to Settings' },
  { keys: 'G → T', description: 'Go to Templates' },
  { keys: 'Escape', description: 'Close dialog / clear' },
];

export { SHORTCUTS };

export default function useKeyboardShortcuts() {
  const navigate = useNavigate();
  let gPressed = false;
  let gTimeout = null;

  const handleKeyDown = useCallback((e) => {
    // Ignore if user is typing in an input/textarea
    const tag = e.target.tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) {
      // Allow Escape to blur
      if (e.key === 'Escape') {
        e.target.blur();
      }
      return;
    }

    const ctrl = e.ctrlKey || e.metaKey;

    // Ctrl+K → Focus search
    if (ctrl && e.key === 'k') {
      e.preventDefault();
      const searchInput = document.querySelector('input[placeholder*="Search"]');
      if (searchInput) searchInput.focus();
      return;
    }

    // Ctrl+/ → Show shortcuts
    if (ctrl && e.key === '/') {
      e.preventDefault();
      // Dispatch custom event that a shortcuts dialog can listen to
      window.dispatchEvent(new CustomEvent('show-shortcuts'));
      return;
    }

    // "G" prefix for navigation
    if (e.key === 'g' && !ctrl && !e.altKey) {
      gPressed = true;
      if (gTimeout) clearTimeout(gTimeout);
      gTimeout = setTimeout(() => { gPressed = false; }, 1000);
      return;
    }

    if (gPressed) {
      gPressed = false;
      if (gTimeout) clearTimeout(gTimeout);

      const routes = {
        h: '/home',
        o: '/orders',
        c: '/catalog',
        r: '/reports',
        s: '/settings',
        t: '/templates',
      };

      if (routes[e.key]) {
        e.preventDefault();
        navigate(routes[e.key]);
      }
    }
  }, [navigate]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
